import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ConversationStep } from "../../interfaces";
import DOMPurify from "dompurify";
import TypingAnimation from "../TypingAnimation";

import "./LifeInsuranceIllustration.scss";

interface IllustrationPageProps {
    values?: any;
    step?: ConversationStep;
    triggerNextStep?: (data?: {
        currentStep: ConversationStep;
        value?: string | number;
        trigger: string;
    }) => void;
    setDataValues?: (values: any) => void;
    currentStep?: ConversationStep;
}

const {
    REACT_APP_SHOP_URL,
    REACT_APP_COMMS_URL,
    REACT_APP_INSURANCE_URL,
    REACT_APP_TOKEN_PASSCODE,
} = process.env;

const DelayedMessage = ({
    step,
    message,
    delay,
    onClick,
}: {
    step: ConversationStep;
    message: string;
    delay: number;
    onClick: Function;
}) => {
    const [isShown, setIsShown] = useState(false);
    const [isClicked, setIsClicked] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setIsShown(true);
        }, delay);
        /*eslint-disable react-hooks/exhaustive-deps */
    }, []);
    //
    return isShown ? (
        <div className="conversation-message-bubble life-insurance-bubble">
            <TypingAnimation theme={step?.theme!} type={step?.type!} />
            <div
                className="conversation-message-bubble-text"
                style={{ cursor: "pointer" }}
                onClick={() => {
                    if (!isClicked) {
                        onClick();
                        setIsClicked(true);
                    }
                }}
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(message),
                }}
            />
        </div>
    ) : null;
};

const LifeInsuranceIllustration: React.FC<IllustrationPageProps> = ({
    values,
    step,
    triggerNextStep,
    currentStep,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [resultText, setResultText] = useState<string[]>([]);
    const [illustrationLink, setIllustrationList] = useState("");
    const [showFindOutMore, setShowFindOutMore] = useState(false);

    const calculateDOB = (age: number) => {
        const year = new Date().getFullYear();
        const diff = year - age;
        return `${diff}-${(`0` + (new Date().getMonth() + 1)).slice(-2)}-${(
            `0` + new Date().getDate()
        ).slice(-2)}`;
    };

    const parseBody = () => {
        const {
            theme,
            isSmoker,
            age,
            applicant1_gender,
            employment,
            postcode,
            applicant1_firstName,
            applicant1_lastName,
            applicant1_email,
            mortgage_balance,
            ...rest
        } = values;
        const body = {
            apiKey: theme?.apiKey,
            version: theme?.version,
            clientDetails: {
                firstLife: {
                    dateOfBirth: calculateDOB(age),
                    isSmoker: isSmoker === "Yes" ? true : false,
                    gender: applicant1_gender,
                    employmentDetails: {
                        ...((employment === "Employed" ||
                            employment === "SelfEmployed" ||
                            employment === "Contractor") && {
                            income: rest?.income,
                            origoOccupationCode: rest?.occupation?.code,
                        }),
                        employment: employment === "Contractor" ? "SelfEmployed" : employment,
                    },
                    postcode,
                    forename: applicant1_firstName,
                    surname: applicant1_lastName,
                    email: applicant1_email,
                },
                ...(values?.partner_covered === "Yes" && {
                    secondLife: {
                        dateOfBirth: calculateDOB(values?.partner_age),
                        isSmoker: values?.partner_smoker === "Yes" ? true : false,
                        employmentDetails: {
                            ...((values?.partner_employment === "Employed" ||
                                values?.partner_employment === "SelfEmployed" ||
                                values?.partner_employment === "Contractor") && {
                                income: values?.partner_income,
                                origoOccupationCode: values?.partner_occupation?.code,
                            }),
                            employment:
                                values?.partner_employment === "Contractor"
                                    ? "SelfEmployed"
                                    : values?.partner_employment,
                        },
                        postcode,
                    },
                }),
            },
            benefitDetails: {
                livesAssured: "First",
                lifeCoverAmount: parseFloat(mortgage_balance) || 250000,
                term: 20,
            },
            ...rest,
        };

        return body;
    };

    const getInitialInsurance = async () => {
        const blockItems = [
            "getFamilyIncomeBenefit",
            "getDecreasingTerm",
            "getDecreasingTermCritical",
            "getWholeOfLife",
            "getWholeOfLifeOverFifty",
            "getLevelTerm",
            "getLevelTermCritical",
            "getIncomeProtection",
        ];

        const {
            data: { result: ipipelineResult },
        } = await axios.post(
            `${REACT_APP_COMMS_URL}/api/ipipeline?block=${blockItems.join(",")}`,
            parseBody()
        );

        if (
            !ipipelineResult?.getLevelTerm?.premium &&
            !ipipelineResult?.getDecreasingTerm?.premium &&
            !ipipelineResult?.getWholeOfLife?.premium &&
            !ipipelineResult?.getWholeOfLifeOverFifty?.premium &&
            !ipipelineResult?.getFamilyIncomeBenefit?.premium &&
            !ipipelineResult?.getIncomeProtection?.premium
        ) {
            throw new Error(
                "Unfortunately, we are unable to get appropriate plans or insurances based on your inputs."
            );
        } else {
            const arrResults = ["Looks like you can get"];

            //disable for brookson
            if (
                ipipelineResult?.getLevelTerm?.premium > 0 &&
                values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a"
            ) {
                arrResults.push(
                    `Level Term Life Insurance from £${ipipelineResult?.getLevelTerm?.premium} per month.`
                );
            }

            //disable for brookson
            if (
                ipipelineResult?.getLevelTermCritical?.premium > 0 &&
                values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a"
            ) {
                arrResults.push(
                    `Level Term Critical Illness Cover from £${ipipelineResult?.getLevelTerm?.premium} per month.`
                );
            }

            if (ipipelineResult?.getDecreasingTerm?.premium > 0) {
                arrResults.push(
                    `Decreasing Term Life Insurance from £${ipipelineResult?.getDecreasingTerm?.premium} per month.`
                );
            }

            //disable for brookson
            if (
                ipipelineResult?.getDecreasingTermCritical?.premium > 0 &&
                values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a"
            ) {
                arrResults.push(
                    `Decreasing Term Critical Illness Cover from £${ipipelineResult?.getDecreasingTerm?.premium} per month.`
                );
            }

            //disable for brookson
            if (
                ipipelineResult?.getWholeOfLife?.premium > 0 &&
                values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a"
            ) {
                arrResults.push(
                    `Whole of Life Cover from £${ipipelineResult?.getWholeOfLife?.premium} per month.`
                );
            }

            //disable for brookson
            if (
                ipipelineResult?.getWholeOfLifeOverFifty?.premium > 0 &&
                values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a"
            ) {
                arrResults.push(
                    `Whole of Life Over Fifty Cover from £${ipipelineResult?.getWholeOfLifeOverFifty?.premium} per month.`
                );
            }

            //disable for brookson
            if (
                ipipelineResult?.getFamilyIncomeBenefit?.premium > 0 &&
                values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a"
            ) {
                arrResults.push(
                    `Family Income Benefit from £${ipipelineResult?.getWholeOfLifeOverFifty?.premium} per month.`
                );
            }

            //disable for brookson
            if (
                ipipelineResult?.getIncomeProtection?.premium > 0 &&
                values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a"
            ) {
                arrResults.push(
                    `You could also look at Income Protection designed to pay out if you stop working, from £${ipipelineResult?.getIncomeProtection?.premium} per month.`
                );
            }

            if (values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a") {
                arrResults.push(`I've put together your full illustration here. Take a look 😊`);
            } else {
                arrResults.push(
                    "Book in for a full adviser appointment to fully discuss your requirements"
                );
            }

            setResultText(arrResults);
        }
    };

    const saveToRedis = async () => {
        const { theme, ...rest } = values;
        const { data } = await axios.post(`${REACT_APP_SHOP_URL}/api/v1/shop/client/save`, {
            ...rest,
            apiKey: theme?.apiKey,
        });
        if (!data) {
            throw new Error("Unable to generate your illustration link.");
        } else {
            //disable for brookson
            if (values?.theme?.apiKey !== "f9f88505-e6ea-4721-aeda-10b52c15880a") {
                setShowFindOutMore(true);
            } else {
                setTimeout(() => {
                    //direct trigger appointment
                    triggerNextStep!({
                        currentStep: currentStep!,
                        trigger: "select_date",
                    });
                }, resultText.length * 1000);
            }
        }
    };

    const initialize = useCallback(async () => {
        try {
            await getInitialInsurance();
            await saveToRedis();
        } catch (err: any) {
            setResultText(err?.message);
        } finally {
            setIsLoading(false);
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, []);

    useEffect(() => {
        initialize();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [initialize]);

    return (
        <>
            {isLoading ? null : (
                <div className="result-text-container">
                    {resultText.map((text, index) => (
                        <DelayedMessage
                            key={index}
                            delay={(index + 1) * 1000}
                            step={step!}
                            message={text}
                            onClick={() => {}}
                        />
                    ))}

                    {showFindOutMore && (
                        <div className="illustration-link">
                            <DelayedMessage
                                delay={resultText.length * 1000}
                                step={step!}
                                message={"Find out more"}
                                onClick={() => {
                                    triggerNextStep!({
                                        currentStep: currentStep!,
                                        trigger: "select_date",
                                    });
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default LifeInsuranceIllustration;
